import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

const field =
  "w-full bg-transparent border-b border-olive/25 rounded-none px-0 py-3 focus:border-olive focus:outline-none text-base placeholder:text-ink/40 transition-colors";
const labelC = "overline text-bronze block mb-2";

export default function Auth({ mode = "login" }) {
  const isSignup = mode === "signup";
  useSeo({ title: isSignup ? "Sign Up" : "Login" });
  const { login, signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "", name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = isSignup
        ? await signup(form)
        : await login(form.username, form.password);
      toast.success(isSignup ? "Welcome to Sutra Interio!" : "Welcome back!");
      navigate(user.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="pt-32 md:pt-40 pb-24 min-h-screen">
      <div className="mx-auto max-w-md px-6">
        <span className="overline text-bronze">{isSignup ? "Create account" : "Member access"}</span>
        <h1 className="font-display text-5xl mt-3 mb-8 text-olive">
          {isSignup ? "Join Sutra" : "Welcome back"}
        </h1>
        <form onSubmit={submit} className="space-y-6" data-testid="auth-form">
          {isSignup && (
            <div>
              <label className={labelC}>Full Name</label>
              <input data-testid="auth-name" className={field} value={form.name} onChange={up("name")} placeholder="Your name" />
            </div>
          )}
          <div>
            <label className={labelC}>Username*</label>
            <input data-testid="auth-username" className={field} value={form.username} onChange={up("username")} placeholder="username" autoCapitalize="none" />
          </div>
          {isSignup && (
            <div>
              <label className={labelC}>Email</label>
              <input data-testid="auth-email" type="email" className={field} value={form.email} onChange={up("email")} placeholder="you@email.com" />
            </div>
          )}
          <div>
            <label className={labelC}>Password*</label>
            <input data-testid="auth-password" type="password" className={field} value={form.password} onChange={up("password")} placeholder="••••••" />
          </div>
          <button
            type="submit"
            data-testid="auth-submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-olive text-beige text-sm uppercase tracking-[0.2em] font-bold px-8 py-4 rounded-sm hover:bg-[#21463c] transition-colors disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {isSignup ? "Create Account" : "Log In"}
          </button>
        </form>
        <p className="mt-8 text-sm text-ink/60">
          {isSignup ? "Already have an account? " : "New to Sutra Interio? "}
          <Link to={isSignup ? "/login" : "/signup"} className="text-bronze link-underline" data-testid="auth-switch">
            {isSignup ? "Log in" : "Create one"}
          </Link>
        </p>
      </div>
    </div>
  );
}
